// ✅ src/pages/VehicleV2/WorkLocationListing.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/workLocationListing", workLocationListing);
//
// APIs used:
//   GET  https://nissi-india.com/api/workLocationListing/list?branchId=..&page=1&limit=10&search=..
//   POST https://nissi-india.com/api/workLocationListing/delete   body: { ids:[...] }
//
// Routing:
//   Add : /vehicle/WorkLocationMaster
//   Edit: /vehicle/WorkLocationMaster?worklocationid=123
//
// ✅ Updated pattern:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - loading state cleanup
// - useCallback pattern
// - server-side pagination
// - delete selected
// - same UI structure style

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/workLocationListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const WorkLocationListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);

  const pages = useMemo(() => {
    const p = Math.ceil((total || 0) / (limit || 10));
    return p <= 0 ? 1 : p;
  }, [total, limit]);

  const selectAll = useMemo(() => {
    if (!rows.length) return false;
    return rows.every((r) => selectedIds.includes(r.wlid));
  }, [rows, selectedIds]);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const fetchList = useCallback(async () => {
    try {
      if (!toInt(branchId)) {
        toast.error("branchid not found in localStorage");
        setRows([]);
        setTotal(0);
        return;
      }

      setLoading(true);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId: toInt(branchId, 0),
          page,
          limit,
          search: toStr(search),
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load work locations");
      }

      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(toInt(data?.total, 0));

      // clear current page selections after reload
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load work locations"
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [branchId, page, limit, search, loginUserId, commonHeaders]);

  // initial + page/limit changes
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // search debounce -> page reset
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
    }, 300);

    return () => clearTimeout(t);
  }, [search]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(rows.map((r) => r.wlid));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one location to delete");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId,
          branchId: toInt(branchId, 0),
          branchType: branch,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success && !(Array.isArray(data?.deleted) && data.deleted.length > 0)) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data?.message || "Deleted Successfully");

      // if last row of page deleted, move back one page if possible
      if (rows.length === selectedIds.length && page > 1) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        fetchList();
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Delete failed"
      );
      fetchList();
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (wlid) => {
    navigate(`/vehicle/WorkLocationMaster?worklocationid=${wlid}`);
  };

  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK LOCATION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Work Location
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/vehicle/WorkLocationMaster")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Search</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>

                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name/code/address/status..."
                      className={`pl-10 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-4">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <button
                  onClick={() => {
                    setPage(1);
                    setTimeout(() => fetchList(), 0);
                  }}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search Records
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {loading ? (
              <div className="text-center py-10">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Work Locations Found
                </h3>

                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or add a new location.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Address1</th>
                      <th className="px-4 py-4 text-center font-semibold">Address2</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={r.wlid}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.wlname}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.wlcode}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.addr1}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.addr2}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.status}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleEdit(r.wlid)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEdit />
                              Edit
                            </button>

                            <input
                              type="checkbox"
                              checked={selectedIds.includes(r.wlid)}
                              onChange={() => toggleSelect(r.wlid)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination + Delete */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className={`px-3 py-2 rounded border ${
                  page <= 1 || loading
                    ? "bg-gray-300 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 hover:bg-gray-800"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              <span className="text-sm">
                Page <b>{page}</b> / <b>{pages}</b>
              </span>

              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages || loading}
                className={`px-3 py-2 rounded border ${
                  page >= pages || loading
                    ? "bg-gray-300 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 hover:bg-gray-800"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>

              <select
                value={limit}
                onChange={(e) => {
                  setLimit(toInt(e.target.value, 10));
                  setPage(1);
                }}
                disabled={loading}
                className={`ml-2 px-3 py-2 border rounded ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <button
              onClick={handleDelete}
              className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                selectedIds.length === 0 || loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
              disabled={selectedIds.length === 0 || loading}
            >
              <FaTrash />
              Delete Selected
            </button>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : WORK LOCATION LISTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default WorkLocationListing;